/**
 * Glass-styled button for the learner experience.
 * Wraps React Aria Button for press states, focus management, and accessibility.
 *
 * Variants: primary (accent), secondary (outline), gradient (special), ghost, danger
 * Sizes: sm, md, lg
 * Supports: icons (leading/trailing), loading state, disabled, link-style via `href`
 */
import { type ReactNode, type CSSProperties } from 'react';
import {
  Button as AriaButton,
  Link as AriaLink,
  type ButtonProps as AriaButtonProps,
  type LinkProps as AriaLinkProps,
} from 'react-aria-components';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ButtonVariant = 'primary' | 'secondary' | 'accent' | 'gradient' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl' | 'icon';

interface ButtonBaseProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  iconRight?: ReactNode;
  loading?: boolean;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

export type ButtonProps = ButtonBaseProps &
  Omit<AriaButtonProps, 'style' | 'className' | 'children'>;
export type LinkButtonProps = ButtonBaseProps &
  Omit<AriaLinkProps, 'style' | 'className' | 'children'>;

// ─── Size tokens ──────────────────────────────────────────────────────────────

const sizes: Record<
  ButtonSize,
  {
    padding: string;
    fontSize: number | string;
    iconSize: number;
    gap: number;
    radius: number | string;
    minHeight?: number | string;
    width?: number | string;
    height?: number | string;
  }
> = {
  sm: {
    padding: '6px 14px',
    fontSize: 'var(--text-xs)',
    iconSize: 14,
    gap: 5,
    radius: 'var(--radius-full)',
    minHeight: 'var(--control-height-sm)',
  },
  md: {
    padding: '9px 18px',
    fontSize: 'var(--text-sm)',
    iconSize: 16,
    gap: 7,
    radius: 'var(--radius-full)',
    minHeight: 'var(--control-height-md)',
  },
  lg: {
    padding: '12px 24px',
    fontSize: 'var(--text-base)',
    iconSize: 17,
    gap: 8,
    radius: 'var(--radius-full)',
    minHeight: 'var(--control-height-lg)',
  },
  xl: {
    padding: '14px 30px',
    fontSize: 'var(--text-lg)',
    iconSize: 18,
    gap: 9,
    radius: 'var(--radius-full)',
    minHeight: 'var(--control-height-xl)',
  },
  icon: {
    padding: '10px',
    fontSize: 'var(--text-sm)',
    iconSize: 16,
    gap: 0,
    radius: 'var(--radius-full)',
    width: 'var(--control-height-sm)',
    height: 'var(--control-height-sm)',
  },
};

// ─── Variant styles ───────────────────────────────────────────────────────────

function getVariantStyles(
  variant: ButtonVariant,
  size: ButtonSize,
  isHovered: boolean,
  isPressed: boolean
): { base: CSSProperties; transform?: string } {
  const accentShadow =
    size === 'xl' ? 'none' : isHovered ? 'var(--shadow-accent)' : 'var(--shadow-sm)';
  switch (variant) {
    case 'accent':
    case 'primary':
      return {
        base: {
          background: isPressed
            ? 'var(--color-accent-hover)'
            : isHovered
              ? 'var(--color-accent-hover)'
              : 'var(--color-accent)',
          color: 'var(--color-text-on-accent)',
          border: '1px solid transparent',
          boxShadow: accentShadow,
        },
        transform: isPressed ? 'scale(0.97)' : isHovered ? 'translateY(-0.5px)' : undefined,
      };

    case 'secondary':
      return {
        base: {
          background: isPressed
            ? 'var(--color-surface-press)'
            : isHovered
              ? 'var(--color-surface-hover)'
              : 'transparent',
          color: 'var(--color-text)',
          border: `1px solid ${isHovered ? 'var(--color-border-strong)' : 'var(--color-border)'}`,
          boxShadow: isHovered ? 'var(--control-shadow-hover)' : 'var(--control-shadow-subtle)',
        },
        transform: isPressed ? 'scale(0.97)' : isHovered ? 'translateY(-0.5px)' : undefined,
      };

    case 'gradient':
      return {
        base: {
          background: 'linear-gradient(135deg, #ff9b4a 0%, #ff6eb1 55%, #8a6cf0 100%)',
          color: '#fff',
          border: 'none',
          boxShadow: isHovered
            ? '0 4px 14px rgba(138,108,240,0.25)'
            : '0 2px 8px rgba(138,108,240,0.15)',
        },
        transform: isPressed ? 'scale(0.97)' : isHovered ? 'scale(1.02)' : undefined,
      };

    case 'ghost':
      return {
        base: {
          background: isPressed
            ? 'var(--color-surface-press)'
            : isHovered
              ? 'var(--color-surface-hover)'
              : 'transparent',
          color: 'var(--color-text-secondary)',
          border: '1px solid transparent',
          boxShadow: 'none',
        },
        transform: isPressed ? 'scale(0.97)' : undefined,
      };

    case 'danger':
      return {
        base: {
          background: isPressed
            ? 'var(--color-error-hover)'
            : isHovered
              ? 'var(--color-error-hover)'
              : 'var(--color-error)',
          color: 'var(--color-text-on-accent)',
          border: '1px solid transparent',
          boxShadow: isHovered
            ? '0 4px 12px rgba(212,68,112,0.3)'
            : '0 2px 8px rgba(212,68,112,0.15)',
        },
        transform: isPressed ? 'scale(0.97)' : isHovered ? 'translateY(-0.5px)' : undefined,
      };
  }
}

// ─── Loading spinner ──────────────────────────────────────────────────────────

function Spinner({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      style={{ animation: 'f-spin 0.8s linear infinite', flexShrink: 0 }}
    >
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" opacity="0.25" />
      <path d="M14 8a6 6 0 0 0-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

// ─── Shared render logic ──────────────────────────────────────────────────────

function renderContent(
  {
    variant = 'primary',
    size = 'md',
    icon,
    iconRight,
    loading,
    children,
    style: userStyle,
  }: ButtonBaseProps,
  renderProps: {
    isHovered: boolean;
    isPressed: boolean;
    isFocusVisible: boolean;
    isDisabled: boolean;
  }
) {
  const s = sizes[size];
  const { base, transform } = getVariantStyles(
    variant,
    size,
    renderProps.isHovered,
    renderProps.isPressed
  );

  const combinedStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: s.gap,
    padding: s.padding,
    width: s.width,
    height: s.height,
    minHeight: s.minHeight,
    borderRadius: s.radius,
    fontSize: s.fontSize,
    fontWeight: 'var(--weight-semibold)',
    fontFamily: 'var(--font-sans)',
    letterSpacing: 'var(--tracking-normal)',
    cursor: renderProps.isDisabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.15s',
    outline: 'none',
    opacity: renderProps.isDisabled ? 0.5 : 1,
    transform,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    ...base,
    // Focus ring
    ...(renderProps.isFocusVisible
      ? {
          outline: '2px solid var(--color-accent)',
          outlineOffset: 2,
        }
      : {}),
    ...userStyle,
  };

  return {
    style: combinedStyle,
    children: (
      <>
        {loading ? <Spinner size={s.iconSize} /> : icon}
        {children}
        {iconRight}
      </>
    ),
  };
}

// ─── Button ───────────────────────────────────────────────────────────────────

export function Button({
  variant = 'primary',
  size = 'md',
  icon,
  iconRight,
  loading,
  className,
  style,
  children,
  isDisabled,
  ...ariaProps
}: ButtonProps) {
  return (
    <AriaButton {...ariaProps} isDisabled={isDisabled || loading} className={className}>
      {(renderProps) => {
        const rendered = renderContent(
          { variant, size, icon, iconRight, loading, children, style },
          renderProps
        );
        return <span style={rendered.style}>{rendered.children}</span>;
      }}
    </AriaButton>
  );
}

// ─── LinkButton ───────────────────────────────────────────────────────────────
// Same visual as Button but renders as a React Aria Link (for navigation).

export function LinkButton({
  variant = 'primary',
  size = 'md',
  icon,
  iconRight,
  loading,
  className,
  style,
  children,
  ...ariaProps
}: LinkButtonProps) {
  return (
    <AriaLink {...ariaProps} className={className}>
      {(renderProps) => {
        const rendered = renderContent(
          { variant, size, icon, iconRight, loading, children, style },
          renderProps
        );
        return <span style={rendered.style}>{rendered.children}</span>;
      }}
    </AriaLink>
  );
}
